import { useState } from "react";
import { Pencil } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { brl } from "@/lib/api";
import { ImportItemEdit, itemError } from "@/components/product/import/ImportItemEdit";
import { ImportCategoryHeader } from "@/components/product/import/ImportCategoryHeader";

function ItemView({ i, c, on, onToggle, onEdit }) {
  const err = itemError(i, c.name);
  const dup = i.exists && i.name.trim().toLowerCase() === i.orig.trim().toLowerCase();
  return (
    <div className="flex items-start gap-1 pr-2 transition-colors hover:bg-muted/30">
      <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-3 px-3 py-2.5" data-testid={`menu-import-item-${i.key}`}>
        <Checkbox checked={on} onCheckedChange={onToggle} className="mt-0.5" data-testid={`menu-import-item-check-${i.key}`} />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium" data-testid={`menu-import-item-name-${i.key}`}>{i.name}</span>
            {dup && <Badge variant="outline" className="text-[10px]">Já cadastrado</Badge>}
            {i.cat && <Badge variant="secondary" className="text-[10px]" data-testid={`menu-import-item-moved-${i.key}`}>Vai para: {i.cat}</Badge>}
          </span>
          {i.description && <span className="line-clamp-2 break-words text-xs text-muted-foreground">{i.description}</span>}
          {i.groups.map((g) => <span key={g.name} className="mr-1 mt-1 inline-block max-w-full break-words rounded bg-muted px-1.5 py-0.5 text-[11px]">{g.name}: {g.options.map((o) => o.name).join(", ")}</span>)}
          {err && <span className="block text-xs text-red-500">{err}</span>}
        </span>
        <span className="shrink-0 font-mono text-sm" data-testid={`menu-import-item-price-${i.key}`}>{brl(i.price)}</span>
      </label>
      <Button variant="ghost" size="icon" className="mt-1.5 h-8 w-8 shrink-0" onClick={onEdit} title="Corrigir produto" data-testid={`menu-import-item-edit-${i.key}`}><Pencil className="h-3.5 w-3.5" /></Button>
    </div>
  );
}

function ItemRow({ i, c, on, onToggle, found, existing, onEdit }) {
  const [editing, setEditing] = useState(false);
  return (
    <li>
      {editing
        ? <ImportItemEdit item={i} block={c.name} found={found} existing={existing} onCancel={() => setEditing(false)} onSave={(p) => { onEdit(c.key, i.key, p); setEditing(false); }} />
        : <ItemView i={i} c={c} on={on} onToggle={onToggle} onEdit={() => setEditing(true)} />}
    </li>
  );
}

function CategoryBlock({ c, sel, toggle, ctx }) {
  const keys = c.items.map((i) => i.key);
  return (
    <div className="overflow-hidden rounded-xl border" data-testid={`menu-import-cat-${c.key}`}>
      <ImportCategoryHeader c={c} all={keys.every((k) => sel.has(k))} some={keys.some((k) => sel.has(k))} onToggle={(v) => toggle(keys, v)}
        exists={ctx.existingSet.has(c.name.trim().toLowerCase())} onRename={ctx.onRename} />
      <ul className="divide-y">{c.items.map((i) => <ItemRow key={i.key} i={i} c={c} on={sel.has(i.key)} onToggle={(v) => toggle([i.key], !!v)} found={ctx.found} existing={ctx.existing} onEdit={ctx.onEdit} />)}</ul>
    </div>
  );
}

export function ImportReview({ preview, sel, setSel, onEdit, onRename }) {
  const toggle = (keys, on) => setSel((s) => { const n = new Set(s); keys.forEach((k) => (on ? n.add(k) : n.delete(k))); return n; });
  const total = preview.categories.reduce((s, c) => s + c.items.length, 0);
  const found = [...new Set(preview.categories.map((c) => c.name.trim()).filter(Boolean))];
  const lower = new Set(found.map((n) => n.toLowerCase()));
  const sys = preview.existing_categories || [];
  const ctx = { found, existing: sys.filter((n) => !lower.has(n.toLowerCase())), existingSet: new Set(sys.map((n) => n.toLowerCase())), onEdit, onRename };
  return (
    <div className="min-w-0 space-y-3" data-testid="menu-import-review">
      <p className="text-sm" data-testid="menu-import-summary">Encontramos <b>{preview.categories.length}</b> categoria(s) e <b>{total}</b> produto(s)
        {preview.groups.length > 0 && <>, além de <b>{preview.groups.length}</b> grupo(s) de complementos</>}. Produtos que já existem vêm desmarcados. Toque no lápis para corrigir nome, preço, descrição ou categoria.</p>
      {preview.warnings.map((w) => <p key={w} className="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">{w}</p>)}
      {preview.categories.map((c) => <CategoryBlock key={c.key} c={c} sel={sel} toggle={toggle} ctx={ctx} />)}
    </div>
  );
}
